import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ImageMediaControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-media-controls';
import {ItemEdgeEffectsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-edge-effects-option';
import {ItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-shadow-option';
import {ItemBlackAndWhiteOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-black-and-white-option';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {ItemSepiaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-sepia-option';
import {ItemBrightnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-brightness-option';
import {ItemInvertOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-invert-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {ItemGlowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-glow-option';
import {ItemBorderOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-option';
import {ItemRemoveColorOption} from '@Components/contextual-menu/components/item-remove-color-option';
import {ImageItemPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-image-options/poster-editing-image-options.types';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {CustomShadowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/custom-shadow-color-panel';
import {ItemBorderColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-color-panel';
import {ItemGlowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-glow-color-panel';
import {ItemRemoveColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-remove-color-panel';
import {ItemContrastOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-contrast-option';
import {ItemVibranceOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-vibrance-option';
import {ItemSaturationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-saturation-option';
import {ItemTintOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-tint-option';
import {ItemMultiplyOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-multiply-option';
import {ItemGammaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-gamma-option';
import {ItemMultiplyColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-multiply-color-panel';
import {ItemTintColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-tint-color-panel';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {ItemInteractivitySection} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-section';
import {ImageRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/image-roundness-option/image-roundness-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useIsOpenedImagePanelValid} from '@Hooks/poster-editor/useIsOpenedImagePanelValid';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-panel/item-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
export function PosterEditingImageOptions(): ReactElement<any> {
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(ImageItemPanels.NONE);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  useIsOpenedImagePanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel);

  if (!activeItem) {
    throw new Error('Active item does not exist in image options');
  }

  if (!(activeItem.isImage() || activeItem.isSticker())) {
    throw new Error(`Expected item to be of type image/sticker - instead got ${activeItem.gitype}`);
  }

  const getSubPanel = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case ImageItemPanels.SHADOW:
        return <CustomShadowColorPanel />;
      case ImageItemPanels.BORDER:
        return <ItemBorderColorPanel />;
      case ImageItemPanels.GLOW:
        return <ItemGlowColorPanel />;
      case ImageItemPanels.REMOVE_COLOR:
        return <ItemRemoveColorPanel />;
      case ImageItemPanels.MULTIPLY:
        return <ItemMultiplyColorPanel />;
      case ImageItemPanels.TINT:
        return <ItemTintColorPanel />;
      case ImageItemPanels.ANIMATION:
        return <ItemAnimationPanel />;
      default:
        throw new Error(`unhandled sub panel case ${currentlyOpenedPanel} for image item`);
    }
  };

  const getOptions = (): ReactElement<any> => {
    if (isSubPanelOpened()) {
      return getSubPanel();
    }

    switch (currentItemHeaderTab) {
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getPositionOptions = (): ReactElement<any> => {
    return <ItemPositionContextualOptions />;
  };

  const getEditOptionsForIcon = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.ANIMATION);
          }}
        />
        <ContextualMenuHeadingOption id="image-adjustments-heading-option" text={window.i18next.t('pmwjs_adjustments')} />
        <ItemTintOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.TINT);
          }}
        />
        <ItemMultiplyOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.MULTIPLY);
          }}
        />
        <ContextualMenuHeadingOption id="image-effects-heading-option" text={window.i18next.t('pmwjs_effects')} />
        <ItemShadowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.SHADOW);
          }}
        />
        <ItemGlowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.GLOW);
          }}
        />
        <ItemBorderOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.BORDER);
          }}
        />
        <ItemBlackAndWhiteOption />
        <ItemInvertOption />
        <ItemRemoveColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.REMOVE_COLOR);
          }}
        />
        <ItemInteractivitySection />
      </>
    );
  };

  const getEditOptions = (): ReactElement<any> => {
    if (activeItem.isImage() && (activeItem.isIcon() || activeItem.isAiIcon())) {
      return getEditOptionsForIcon();
    }
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />

        {activeItem.isImage() && activeItem.isDrawing() ? (
          <ItemTintOption
            onClickColorFill={(): void => {
              setCurrentlyOpenedPanel(ImageItemPanels.TINT);
            }}
          />
        ) : null}
        {activeItem.isImage() && !activeItem.isDrawing() ? <ImageMediaControls /> : null}
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.ANIMATION);
          }}
        />
        <ContextualMenuHeadingOption id="image-effects-heading-option" text={window.i18next.t('pmwjs_effects')} />
        <ItemEdgeEffectsOption />
        <ItemShadowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.SHADOW);
          }}
        />
        <ItemGlowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.GLOW);
          }}
        />
        <ItemBorderOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.BORDER);
          }}
        />
        <ImageRoundnessOption />
        <ItemBlackAndWhiteOption />
        <ItemSepiaOption />
        <ItemInvertOption />
        <ItemRemoveColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.REMOVE_COLOR);
          }}
        />
        <ContextualMenuHeadingOption id="image-adjustments-heading-option" text={window.i18next.t('pmwjs_adjustments')} />
        <ItemBrightnessOption />
        <ItemContrastOption />
        <ItemVibranceOption />
        <ItemSaturationOption />
        {activeItem.isImage() && activeItem.isDrawing() ? null : (
          <ItemTintOption
            onClickColorFill={(): void => {
              setCurrentlyOpenedPanel(ImageItemPanels.TINT);
            }}
          />
        )}
        <ItemMultiplyOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(ImageItemPanels.MULTIPLY);
          }}
        />
        <ItemGammaOption />
        <ItemInteractivitySection />
      </>
    );
  };

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedPanel !== ImageItemPanels.NONE;
  };

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === ImageItemPanels.ANIMATION) {
      return window.i18next.t('pmwjs_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | null => {
    if (isSubPanelOpened() || currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return (
        <ContextualMenuHeader id="image-sub-panel">
          <ContextualMenuTextHeader
            showBackButton
            onBackButton={(): void => {
              if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
                setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
              }

              setCurrentlyOpenedPanel(ImageItemPanels.NONE);
            }}
            text={getHeaderText()}
          />
        </ContextualMenuHeader>
      );
    }

    return null;
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.IMAGE_DEFAULT_POSITION : ContextualMenuContentKeys.IMAGE_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu contentKey={getKey()} id="image-contextual-options" includeLeftRightPadding={!isSubPanelOpened()} header={getHeader()}>
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlyOpenedPanel, activeItemId]);
}
